/**
 * Per-user "recently opened" timestamps for the projects grid.
 *
 * WHY THIS EXISTS
 * The "Last update" sort option has always been wired to the project's *creation*
 * date — the backend sends `Modified: created` (project_routes.py / utility_routes.py),
 * so the sort could never change. Nothing in the system records activity at all.
 * This module is phase 1 of fixing that: it records project opens client-side so
 * the grid can surface what you actually worked on recently.
 *
 * WHY localStorage AND NOT IndexedDB
 * The read happens inside a synchronous sort (`filteredProjects` in ProjectsList).
 * localStorage reads synchronously, so the map is present on first paint. IndexedDB
 * is promise-based and would render the old order first, then visibly reshuffle a
 * tick later — the wrong trade for a feature whose whole point is "the right project
 * is already at the top". Capacity is a non-issue: id -> ISO string is ~30 bytes, so
 * the 5MB budget covers ~150k projects.
 *
 * KNOWN LIMIT
 * This is per-browser. Opening a project on your laptop does not reorder your
 * desktop's grid, and clearing site data resets it. Fixing that needs the server-side
 * phase (see docs/superpowers/specs/2026-08-04-project-last-activity-sort-design.md),
 * which writes projectusers.last_activity. This module is built to coexist with it:
 * the consumer takes max(serverValue, localValue), so when the backend lands the
 * server wins wherever it is fresher and no code here has to be removed.
 */

const STORAGE_KEY = 'rsaProjectActivity';

/**
 * Keep the per-user map bounded. Nobody sorts meaningfully by the 500th
 * most-recently-opened project, and an unbounded map would grow for the life of the
 * browser profile.
 */
const MAX_ENTRIES_PER_USER = 500;

/**
 * Mirrors the email resolution already used by getProjectsCacheKey (ProjectsList)
 * and ProjectView, so a given person maps to the same bucket everywhere. Keyed by
 * user so two accounts sharing a machine don't inherit each other's ordering.
 */
export const resolveUserEmail = (currentUser) => {
  const email =
    currentUser?.attributes?.email ||
    currentUser?.email ||
    currentUser?.Email ||
    sessionStorage.getItem('userEmail') ||
    localStorage.getItem('userEmail') ||
    'anonymous';
  return String(email).trim().toLowerCase() || 'anonymous';
};

const readAll = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (error) {
    // Corrupt JSON, or storage blocked (Safari private mode). Sorting must still
    // work, so degrade to "no activity recorded" rather than throwing into render.
    console.warn('projectActivity: could not read store, ignoring', error);
    return {};
  }
};

const prune = (map) => {
  const entries = Object.entries(map);
  if (entries.length <= MAX_ENTRIES_PER_USER) return map;
  // ISO-8601 strings sort lexicographically, so plain string compare is correct here.
  entries.sort((a, b) => (a[1] < b[1] ? 1 : a[1] > b[1] ? -1 : 0));
  return Object.fromEntries(entries.slice(0, MAX_ENTRIES_PER_USER));
};

/**
 * @returns {Object<string, string>} projectId -> ISO timestamp, for this user only.
 *   Always an object; never throws.
 */
export const getProjectActivity = (email) => {
  const forUser = readAll()[email];
  return forUser && typeof forUser === 'object' ? forUser : {};
};

/**
 * Record that a project was just opened. Fire-and-forget: any failure is logged and
 * swallowed, because a storage problem must never block navigation.
 */
export const recordProjectOpened = (projectId, email) => {
  if (projectId === undefined || projectId === null || projectId === '') return;
  try {
    const all = readAll();
    all[email] = prune({
      ...(all[email] || {}),
      [String(projectId)]: new Date().toISOString(),
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (error) {
    console.warn('projectActivity: could not record open', error);
  }
};
